import { useMemo, useCallback } from "react";
import { useURLSearchParams } from "./useURLSearchParams";

// URLパラメータのキー
const SEARCH_KEYS = {
  NAME: "name",
  PHONETIC_NAME: "phonetic_name",
  LAT: "lat",
  LON: "lon",
  SORT_FIELD: "sort_field",
  SORT_ORDER: "sort_order",
  PUBLISHED_REVISION_ID: "published_revision_id",
  HAS_UNPUBLISHED_CHANGES: "has_unpublished_changes"
};

// Public用の型定義
export interface PublicSearchConditions {
  name: string | null;
  phoneticName: string | null;
  location: { lat: number | null; lon: number | null };
  sort: { field: string | null; order: string | null };
}

// Draft用の型定義 (Publicを継承)
export interface DraftSearchConditions extends PublicSearchConditions {
  publishedRevisionId: string | null;
  hasUnpublishedChanges: string | null;
}

// ネストされた更新オブジェクトをフラットなURLパラメータ用オブジェクトに変換する

// キーのマッピングを定義
const keyMapping: Record<string, string> = {
  phoneticName: SEARCH_KEYS.PHONETIC_NAME,
  publishedRevisionId: SEARCH_KEYS.PUBLISHED_REVISION_ID,
  hasUnpublishedChanges: SEARCH_KEYS.HAS_UNPUBLISHED_CHANGES
};

const flattenUpdates = (updates: Record<string, any>) => {
  const flattened: Record<string, any> = {};
  for (const [key, value] of Object.entries(updates)) {
    if (key === "location" && typeof value === "object" && value !== null) {
      flattened[SEARCH_KEYS.LAT] = value.lat;
      flattened[SEARCH_KEYS.LON] = value.lon;
    } else if (key === "sort" && typeof value === "object" && value !== null) {
      flattened[SEARCH_KEYS.SORT_FIELD] = value.field;
      flattened[SEARCH_KEYS.SORT_ORDER] = value.order;
    } else if (key in keyMapping) {
      flattened[keyMapping[key]] = value;
    } else {
      // nameなどはそのまま
      flattened[key] = value;
    }
  }
  return flattened;
};

export const useSpotSearch = () => {
  const { urlParams, updateSearchParams } = useURLSearchParams();

  const searchConditions: DraftSearchConditions = useMemo(
    () => ({
      name: urlParams[SEARCH_KEYS.NAME] || null,
      phoneticName: urlParams[SEARCH_KEYS.PHONETIC_NAME] || null,
      location: {
        lat: urlParams[SEARCH_KEYS.LAT] ? Number(urlParams[SEARCH_KEYS.LAT]) : null,
        lon: urlParams[SEARCH_KEYS.LON] ? Number(urlParams[SEARCH_KEYS.LON]) : null
      },
      sort: {
        field: urlParams[SEARCH_KEYS.SORT_FIELD] || null,
        order: urlParams[SEARCH_KEYS.SORT_ORDER] || null
      },
      publishedRevisionId: urlParams[SEARCH_KEYS.PUBLISHED_REVISION_ID] || null,
      hasUnpublishedChanges: urlParams[SEARCH_KEYS.HAS_UNPUBLISHED_CHANGES] || null
    }),
    [urlParams]
  );

  const updateSearchConditions = useCallback(
    (updates: Record<string, any>) => {
      const flattenedUpdates = flattenUpdates(updates);
      // 検索条件が変更された場合は1ページ目に戻す
      const newUpdates = { ...flattenedUpdates, page: 0 };
      updateSearchParams(newUpdates);
    },
    [updateSearchParams]
  );

  return { searchConditions, updateSearchConditions };
};
